import { Injectable, inject } from '@angular/core';
import { AlertService } from '@core/services/alert.service';
import {
  ApiException,
  ApiResponse,
  CancelReservedOrderItemsAndSubscriptionsRequestDto,
  OrderFinancialTransactionRequestDto,
} from '@core/services/apiclient';
import { Observable, catchError, filter, map, of, take } from 'rxjs';
import {
  CancelReservedResult,
  DEFAULT_ERROR_MESSAGES,
  PaymentResult,
  RefundResult,
  SUCCESS_MESSAGES,
  TRANSACTION_ERROR_CONFIG,
  TransactionResult,
  TransactionType,
} from '../models/orders-transaction.model';
import { TreeListRowData } from '../models/orders.model';
import { OrdersCancelService } from './orders-cancel.service';
import { OrdersDialogService } from './orders-dialog.service';
import { OrdersTreeListRefreshService } from './orders-tree-list-refresh.service';
import { OrdersService } from './orders.service';
import { PaymentTransactionService } from './payment-transaction.service';

@Injectable({
  providedIn: 'root',
})
export class OrdersProcessFacade {
  private readonly dialogService = inject(OrdersDialogService);
  private readonly ordersCancelService = inject(OrdersCancelService);
  private readonly ordersService = inject(OrdersService);
  private readonly alertService = inject(AlertService);
  private readonly treeListRefreshService = inject(OrdersTreeListRefreshService);
  private readonly paymentTransactionService = inject(PaymentTransactionService);

  payOrderItems(orderItems: TreeListRowData[]): void {
    this.dialogService
      .openPaymentModal(orderItems)
      .pipe(
        take(1),
        filter(({ confirmed, response }) => confirmed && !!response),
      )
      .subscribe();
  }

  refundOrderItems(orderItems: TreeListRowData[]): void {
    this.dialogService
      .openRefundModal(orderItems)
      .pipe(
        take(1),
        filter(({ confirmed, response }) => confirmed && !!response),
      )
      .subscribe();
  }

  cancelReservedOrderItems(orderItems: TreeListRowData[]): void {
    this.ordersCancelService.cancelReservedSelection(orderItems, (request) =>
      this.cancelOrderItemsAndSubscriptions(request).subscribe(),
    );
  }

  cancelOrderItemsAndSubscriptions(
    request: CancelReservedOrderItemsAndSubscriptionsRequestDto,
  ): Observable<CancelReservedResult> {
    return this.executeTransaction<CancelReservedResult>(
      'cancelReserved',
      this.ordersService.cancelOrderItemsAndSubscriptions(request),
      request.orders.length,
    );
  }

  payOrderItemsAndSubscriptions(
    selection: TreeListRowData[],
    financialTransactions: OrderFinancialTransactionRequestDto[],
  ): Observable<PaymentResult> {
    const request = this.paymentTransactionService.buildPaymentRequest(
      selection,
      financialTransactions,
    );
    return this.executeTransaction<PaymentResult>(
      'payment',
      this.ordersService.payOrderItemsAndSubscriptions(request),
      request.orders.length,
    );
  }

  refundOrderItemsAndSubscriptions(
    selection: TreeListRowData[],
    financialTransactions: OrderFinancialTransactionRequestDto[],
  ): Observable<RefundResult> {
    const request = this.paymentTransactionService.buildRefundRequest(
      selection,
      financialTransactions,
    );
    return this.executeTransaction<RefundResult>(
      'refund',
      this.ordersService.refundOrderItemsAndSubscriptions(request),
      request.orders.length,
    );
  }

  private executeTransaction<TResult extends TransactionResult<unknown>>(
    type: TransactionType,
    request$: Observable<ApiResponse<unknown>>,
    ordersCount: number,
  ): Observable<TResult> {
    return request$.pipe(
      map((response) => {
        if (response?.status === 200) {
          this.showTransactionSuccess(type, ordersCount);
          this.treeListRefreshService.refresh();
        }
        return { success: true, response } as TResult;
      }),
      catchError((error: unknown) => {
        this.showTransactionError(type, error);
        const isPartialSuccess = ApiException.isApiException(error) && error.status === 207;
        if (isPartialSuccess) {
          this.treeListRefreshService.refresh();
        }
        return of({
          success: false,
          response: ApiException.isApiException(error)
            ? ({ status: error.status, result: error.result } as ApiResponse<unknown>)
            : null,
          error,
        } as TResult);
      }),
    );
  }

  private showTransactionSuccess(type: TransactionType, ordersCount: number): void {
    this.alertService.showCustom(SUCCESS_MESSAGES[type], 'success');
  }

  private showTransactionError(type: TransactionType, error: unknown): void {
    const defaultConfig = {
      message: DEFAULT_ERROR_MESSAGES[type],
      level: 'danger' as const,
    };

    if (!ApiException.isApiException(error)) {
      this.alertService.showApiError(error, {
        fallback: defaultConfig.message,
        type: defaultConfig.level,
      });
      return;
    }

    const config = TRANSACTION_ERROR_CONFIG[type][error.status] ?? defaultConfig;
    if (error.status === 207) {
      // 207 = succès partiel : le corps n'est pas un ProblemDetails, on garde le toast dédié.
      this.alertService.showCustom(config.message, config.level);
      return;
    }
    this.alertService.showApiError(error, { fallback: config.message, type: config.level });
  }
}
